import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Plane, MapPin, Calendar, Users, Car, DollarSign, Clock, Briefcase, Phone, Check, Navigation, Loader2 } from "lucide-react";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import GoogleMapView from "@/components/GoogleMapView";

// Lazy load map component
const MapComponent = lazy(() => import("@/components/MapComponent"));

export default function AirportPickup() {
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    pickup_location: "",
    pickup_address: "",
    dropoff_location: "",
    dropoff_address: "",
    dropoff_zip: "",
    pickup_datetime: "",
    flight_number: "",
    passengers: 1,
    luggage: 1,
    special_requests: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const airports = [
    // Active service area — New York only.
    { code: "JFK", name: "John F. Kennedy International", city: "New York", lat: 40.6413, lng: -73.7781, available: true },
    { code: "LGA", name: "LaGuardia",                    city: "New York", lat: 40.7769, lng: -73.8740, available: true },
    // Coming soon — disabled in the selector. Backend will also reject these.
    { code: "LAX", name: "Los Angeles International",        city: "Los Angeles",    lat: 33.9416, lng: -118.4085, available: false },
    { code: "ORD", name: "O'Hare International",             city: "Chicago",        lat: 41.9742, lng: -87.9073,  available: false },
    { code: "DFW", name: "Dallas/Fort Worth International",  city: "Dallas",         lat: 32.8998, lng: -97.0403,  available: false },
    { code: "MIA", name: "Miami International",              city: "Miami",          lat: 25.7959, lng: -80.2870,  available: false },
    { code: "ATL", name: "Hartsfield-Jackson Atlanta",       city: "Atlanta",        lat: 33.6407, lng: -84.4277,  available: false },
    { code: "SFO", name: "San Francisco International",      city: "San Francisco",  lat: 37.6213, lng: -122.3790, available: false },
  ];

  const [showUnavailable, setShowUnavailable] = useState(false);

  const handleAirportSelect = (code) => {
    const airport = airports.find(a => a.code === code);
    if (!airport) return;
    if (!airport.available) {
      // Outside NY — show the friendly availability message instead of proceeding.
      setShowUnavailable(true);
      updateField("pickup_location", "");
      updateField("pickup_address", "");
      setPickupCoords(null);
      return;
    }
    setShowUnavailable(false);
    updateField("pickup_location", code);
    updateField("pickup_address", airport.name);
    setPickupCoords({ lat: airport.lat, lng: airport.lng, name: airport.name });
  };

  const searchAddress = async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/geo/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data.slice(0, 5));
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearching(false);
    }
  };

  const selectAddress = (result) => {
    updateField("dropoff_address", result.display_name);
    updateField("dropoff_location", result.display_name);
    setDropoffCoords({ 
      lat: parseFloat(result.lat), 
      lng: parseFloat(result.lon), 
      name: result.display_name 
    });
    setSearchResults([]);
  };

  const searchByZipCode = async (zip) => {
    updateField("dropoff_zip", zip);
    if (zip.length === 5) {
      try {
        const response = await axios.get(`${API}/geo/zipcode/${zip}`);
        if (response.data.lat) {
          setDropoffCoords({
            lat: response.data.lat,
            lng: response.data.lng,
            name: response.data.display_name
          });
          updateField("dropoff_address", response.data.display_name);
          updateField("dropoff_location", response.data.display_name);
        }
      } catch (error) {
        console.error("ZIP lookup failed:", error);
      }
    }
  };

  const calculateRoute = async () => {
    if (!pickupCoords || !dropoffCoords) {
      toast.error(t("يرجى تحديد الموقعين", "Please select both locations"));
      return;
    }

    setCalculating(true);
    try {
      // Use Google Distance Matrix via backend for real distance + duration + price
      const priceRes = await axios.post(`${API}/rides/calculate-price`, {
        pickup_location: formData.pickup_location,
        pickup_address: formData.pickup_address || `${pickupCoords.lat},${pickupCoords.lng}`,
        dropoff_location: formData.dropoff_location,
        dropoff_address: formData.dropoff_address || `${dropoffCoords.lat},${dropoffCoords.lng}`,
        pickup_datetime: formData.pickup_datetime || new Date().toISOString(),
        flight_number: formData.flight_number || "",
        passengers: formData.passengers,
        luggage: formData.luggage,
        special_requests: formData.special_requests || ""
      });

      const price = priceRes.data;
      setRouteInfo({
        distance_miles: price.estimated_miles,
        duration_minutes: price.duration_minutes,
      });
      setPriceEstimate({
        estimated_price: price.estimated_price,
        estimated_miles: price.estimated_miles,
        estimated_duration: price.duration_minutes,
        duration_text: price.duration_text,
      });
      setStep(2);
    } catch (error) {
      // Server may reject pickup outside NY — show the friendly availability banner.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail?.code === "service_unavailable_in_region") {
        setShowUnavailable(true);
      } else {
        toast.error(t("فشل حساب المسافة", "Failed to calculate distance"));
      }
    } finally {
      setCalculating(false);
    }
  };

  const handleBooking = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/rides/book`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم الحجز بنجاح!", "Booking successful!"));
      navigate("/dashboard");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail?.code === "service_unavailable_in_region") {
        setShowUnavailable(true);
        setStep(1);
      } else {
        toast.error((typeof detail === "string" ? detail : null) || t("فشل الحجز", "Booking failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Car, text: t("سائق عربي موثوق", "Trusted Arab driver") },
    { icon: Briefcase, text: t("مساعدة بالأمتعة", "Luggage assistance") },
    { icon: Phone, text: t("تواصل مباشر", "Direct communication") },
    { icon: Clock, text: t("متابعة رحلتك", "Track your ride") },
  ];

  return (
    <DashboardLayout title={t("استقبال المطار", "Airport Pickup")}>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="airport-pickup">
        {/* Header */}
        <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 to-cyan-600/20"></div>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <Badge className="bg-blue-500/20 text-blue-400 mb-4">
                $5/{t("شهرياً", "month")} {t("أو", "or")} $10/{t("رحلة واحدة", "single ride")}
              </Badge>
              <h2 className="text-2xl font-bold text-white mb-2">{t("خدمة استقبال المطار", "Airport Pickup Service")}</h2>
              <p className="text-slate-400">{t("سائق عربي موثوق يستقبلك ويوصلك لوجهتك", "Trusted Arab driver picks you up")}</p>
            </div>
            <Plane className="w-20 h-20 text-blue-400" />
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {features.map((feature, i) => (
            <div key={i} className="glass-card rounded-xl p-4 text-center">
              <feature.icon className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-sm text-slate-300">{feature.text}</p>
            </div>
          ))}
        </div>

        {/* Service availability — New York only. Shows automatically when the user picks a non-NY airport
            (selector falls back to disabled) or when the backend returns the 403 region-unavailable error. */}
        {showUnavailable && (
          <div className="glass-card rounded-2xl p-6 border border-yellow-500/40" data-testid="service-unavailable">
            <h3 className="text-lg font-bold text-yellow-300 mb-2 flex items-center gap-2">
              <Plane size={20} /> {t("الخدمة متاحة في نيويورك فقط", "Service available in New York only")}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {t(
                "الخدمة متاحة حالياً في ولاية نيويورك فقط. نعمل حالياً على التوسع لتشمل جميع الولايات الأمريكية. إذا كنت ترغب في العمل معنا كسائق أو شريك، يمكنك التقديم والانضمام إلى فريق عرب سند.",
                "Airport pickup service is currently available in New York only. We are actively expanding to all U.S. states. If you would like to work with Arab Sanad as a driver or partner, you are welcome to apply and join our team."
              )}
            </p>
            <Button onClick={() => navigate("/driver-application")} className="btn-primary" data-testid="apply-driver-cta">
              <Car size={16} className="ml-2" />
              {t("انضم كسائق / شريك", "Apply as Driver / Partner")}
            </Button>
          </div>
        )}

        {/* Driver recruitment — always visible. Sets expectations that Arab Sanad is expanding nationwide. */}
        <div className="glass-card rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="driver-recruitment">
          <div className="flex-1">
            <h3 className="text-base font-bold text-white mb-1">
              {t("نتوسع في جميع الولايات الأمريكية", "Expanding across all U.S. states")}
            </h3>
            <p className="text-slate-400 text-sm">
              {t(
                "الخدمة متاحة حالياً في نيويورك فقط. انضم إلى شبكة عرب سند كسائق أو شريك ووسّع الخدمة في منطقتك.",
                "Currently active in New York only. Join the Arab Sanad network as a driver or partner and bring the service to your area."
              )}
            </p>
          </div>
          <Button onClick={() => navigate("/driver-application")} className="btn-secondary whitespace-nowrap" data-testid="apply-driver-recruitment">
            {t("التقديم كسائق", "Apply as Driver")}
          </Button>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="glass-card rounded-2xl p-6">
            {step === 1 ? (
              <div className="space-y-5">
                <h3 className="text-lg font-bold text-white">{t("تفاصيل الرحلة", "Trip Details")}</h3>
                
                {/* Airport Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("المطار", "Airport")}</Label>
                  <select
                    value={formData.pickup_location}
                    onChange={(e) => handleAirportSelect(e.target.value)}
                    className="w-full h-12 bg-slate-900 border border-slate-700 rounded-lg text-white px-3"
                    data-testid="airport-select"
                  >
                    <option value="">{t("اختر المطار", "Select Airport")}</option>
                    {airports.map(airport => (
                      <option key={airport.code} value={airport.code} disabled={!airport.available}>
                        {airport.code} - {airport.name} ({airport.city})
                        {!airport.available && ` — ${t("قريباً", "coming soon")}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">{t("الخدمة متاحة حالياً في ولاية نيويورك فقط.", "Service available in New York only.")}</p>
                </div>

                {/* ZIP Code */}
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("الرمز البريدي للوجهة", "Destination ZIP Code")}</Label>
                  <div className="relative">
                    <Input
                      value={formData.dropoff_zip}
                      onChange={(e) => searchByZipCode(e.target.value)}
                      placeholder="10001"
                      maxLength={5}
                      className="h-12 bg-slate-900 border-slate-700 text-white"
                    />
                    <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  </div>
                </div>

                {/* Address Search via Google Places Autocomplete */}
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("أو ابحث عن العنوان", "Or search address")}</Label>
                  <PlacesAutocompleteInput
                    value={formData.dropoff_address}
                    onChange={(v) => updateField("dropoff_address", v)}
                    onSelect={(d) => {
                      updateField("dropoff_address", d.formatted_address);
                      updateField("dropoff_location", d.formatted_address);
                      if (d.zip_code) updateField("dropoff_zip", d.zip_code);
                      if (d.lat && d.lng) setDropoffCoords({ lat: d.lat, lng: d.lng, name: d.formatted_address });
                    }}
                    placeholder={t("أدخل العنوان...", "Enter address...")}
                    types="address"
                    dataTestId="airport-dropoff-autocomplete"
                  />
                </div>

                {/* Date & Time */}
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("تاريخ ووقت الوصول", "Arrival Date & Time")}</Label>
                  <Input
                    type="datetime-local"
                    value={formData.pickup_datetime}
                    onChange={(e) => updateField("pickup_datetime", e.target.value)}
                    className="h-12 bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                {/* Flight Number */}
                <div className="space-y-2">
                  <Label className="text-slate-300">{t("رقم الرحلة", "Flight Number")} ({t("اختياري", "optional")})</Label>
                  <Input
                    value={formData.flight_number}
                    onChange={(e) => updateField("flight_number", e.target.value)}
                    placeholder="AA123"
                    className="h-12 bg-slate-900 border-slate-700 text-white"
                  />
                </div>

                {/* Passengers & Luggage */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t("المسافرين", "Passengers")}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="6"
                      value={formData.passengers}
                      onChange={(e) => updateField("passengers", parseInt(e.target.value))}
                      className="h-12 bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t("الحقائب", "Luggage")}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={formData.luggage}
                      onChange={(e) => updateField("luggage", parseInt(e.target.value))}
                      className="h-12 bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <Button 
                  onClick={calculateRoute} 
                  className="btn-primary w-full"
                  disabled={!formData.pickup_location || !dropoffCoords || !formData.pickup_datetime || calculating}
                >
                  {calculating ? (
                    <><Loader2 className="ml-2 animate-spin" size={18} />{t("جاري الحساب...", "Calculating...")}</>
                  ) : (
                    <><Navigation className="ml-2" size={18} />{t("احسب المسافة والسعر", "Calculate Distance & Price")}</>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-white">{t("تأكيد الحجز", "Confirm Booking")}</h3>
                
                {/* Price Card */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
                  <p className="text-slate-400 mb-2">{t("السعر المقدر", "Estimated Price")}</p>
                  <p className="text-4xl font-bold text-yellow-500">${priceEstimate?.estimated_price}</p>
                </div>

                {/* Route Info — customer view: only total + distance + time. Internal breakdown is never shown. */}
                <div className="bg-slate-800 rounded-xl p-4 space-y-3" data-testid="trip-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t("المسافة", "Distance")}</span>
                    <span className="text-white font-bold">{routeInfo?.distance_miles} {t("ميل", "miles")}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t("الوقت المتوقع", "Est. Time")}</span>
                    <span className="text-white font-bold">{Math.round(routeInfo?.duration_minutes)} {t("دقيقة", "min")}</span>
                  </div>
                </div>

                {/* Trip Summary */}
                <div className="bg-slate-800 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Plane className="text-blue-400" size={20} />
                    <span className="text-slate-300">{formData.pickup_location} - {pickupCoords?.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="text-green-400" size={20} />
                    <span className="text-slate-300 text-sm">{formData.dropoff_address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="text-yellow-400" size={20} />
                    <span className="text-slate-300">{formData.pickup_datetime}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="text-purple-400" size={20} />
                    <span className="text-slate-300">{formData.passengers} {t("مسافر", "passenger(s)")}, {formData.luggage} {t("حقيبة", "bags")}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => setStep(1)} className="btn-secondary flex-1">
                    {t("تعديل", "Edit")}
                  </Button>
                  <Button onClick={handleBooking} disabled={loading} className="btn-primary flex-1">
                    {loading ? t("جاري الحجز...", "Booking...") : t("تأكيد الحجز", "Confirm")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="glass-card rounded-2xl p-4">
            <h3 className="text-lg font-bold text-white mb-4">{t("الخريطة", "Map")}</h3>
            {(pickupCoords || dropoffCoords) ? (
              <GoogleMapView
                center={dropoffCoords || pickupCoords}
                zoom={pickupCoords && dropoffCoords ? 9 : 12}
                markers={[
                  pickupCoords && { lat: pickupCoords.lat, lng: pickupCoords.lng, title: pickupCoords.name || "Pickup", id: "p" },
                  dropoffCoords && { lat: dropoffCoords.lat, lng: dropoffCoords.lng, title: dropoffCoords.name || "Dropoff", id: "d" },
                ].filter(Boolean)}
                height="h-96"
              />
            ) : (
              <Suspense fallback={
                <div className="h-96 bg-slate-800 rounded-xl flex items-center justify-center">
                  <Loader2 className="animate-spin text-yellow-500" size={32} />
                </div>
              }>
                <MapComponent pickupLocation={pickupCoords} dropoffLocation={dropoffCoords} height="400px" />
              </Suspense>
            )}
            
            {/* Location Info */}
            {(pickupCoords || dropoffCoords) && (
              <div className="mt-4 space-y-2">
                {pickupCoords && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-400">{t("نقطة الانطلاق:", "Pickup:")}</span>
                    <span className="text-slate-300">{pickupCoords.name}</span>
                  </div>
                )}
                {dropoffCoords && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-slate-400">{t("الوجهة:", "Dropoff:")}</span>
                    <span className="text-slate-300 truncate">{dropoffCoords.name}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
